import { TAROT_DATA } from "./tarotArtData";
import { buildCardBackSVG, buildCardFaceSVG, getCardArt } from "./tarotArt";

const SUIT_INDEX = {
  wands: 0,
  cups: 1,
  swords: 2,
  pentacles: 3,
};

const MINOR_RANK_INDEX = {
  ace: 0,
  two: 1,
  three: 2,
  four: 3,
  five: 4,
  six: 5,
  seven: 6,
  eight: 7,
  nine: 8,
  ten: 9,
  page: 10,
  knight: 11,
  queen: 12,
  king: 13,
};

// Bridges a reading card (from tarotEngine) to the input shape expected by getCardArt.
// Reading cards carry: { arcana: "major"|"minor", suit, rankKey, ... }
// Returns SVG inner content (no outer <svg> tag), or "" if the card cannot be resolved.
export function getTarotCardArt(readingCard) {
  if (!readingCard || !readingCard.arcana) return "";

  if (readingCard.arcana === "major") {
    const rank = Number(readingCard.rankKey);
    if (!Number.isInteger(rank)) return "";
    const majorCard = TAROT_DATA.major[rank];
    if (!majorCard) return "";
    return getCardArt({ ...majorCard, type: "major" });
  }

  if (readingCard.arcana === "minor") {
    const suitIdx = SUIT_INDEX[String(readingCard.suit || "").toLowerCase()];
    const pipIdx = MINOR_RANK_INDEX[String(readingCard.rankKey || "").toLowerCase()];
    if (suitIdx === undefined || pipIdx === undefined) return "";
    return getCardArt({ type: "minor", suitIdx, pipIdx });
  }

  return "";
}

// Builds the art-card shape buildCardFaceSVG expects from a reading card.
// User-visible name/num come from the reading card so the SVG stays localized.
function buildArtCard(readingCard) {
  if (!readingCard || !readingCard.arcana) return null;

  if (readingCard.arcana === "major") {
    const rank = Number(readingCard.rankKey);
    if (!Number.isInteger(rank)) return null;
    const data = TAROT_DATA.major[rank];
    if (!data) return null;
    return {
      ...data,
      type: "major",
      suit: null,
      name: readingCard.name || data.name,
      num: readingCard.number || data.num,
    };
  }

  if (readingCard.arcana === "minor") {
    const suitIdx = SUIT_INDEX[String(readingCard.suit || "").toLowerCase()];
    const pipIdx = MINOR_RANK_INDEX[String(readingCard.rankKey || "").toLowerCase()];
    if (suitIdx === undefined || pipIdx === undefined) return null;
    const suit = TAROT_DATA.suits[suitIdx];
    return {
      type: "minor",
      suitIdx,
      pipIdx,
      suit: suit.name,
      element: suit.element,
      art: `minor_${suitIdx}_${pipIdx}`,
      num: readingCard.number || pipIdx + 1,
      roman: TAROT_DATA.pipRomans[pipIdx],
      name: readingCard.name || `${TAROT_DATA.pips[pipIdx]} of ${suit.name}`,
    };
  }

  return null;
}

// Returns a complete <svg>…</svg> string for the card face for a reading card.
export function getTarotCardFaceSvg(readingCard) {
  const artCard = buildArtCard(readingCard);
  if (!artCard) return "";
  return buildCardFaceSVG(artCard);
}

// Returns a complete <svg>…</svg> string for the card back (deck design).
export function getTarotCardBackSvg() {
  return buildCardBackSVG();
}

export { TAROT_DATA, getCardArt, buildCardFaceSVG, buildCardBackSVG };
